<template>
    <div>
        <Head>
            <title>Корзина</title>
        </Head>
        <Header pageName="Корзина" :cart-count="$page.props.cartCount"/>
        <div class="mx-auto max-w-2xl px-4 py-4 sm:px-6 sm:py-8 lg:max-w-7xl relative">
            <div class="grid points-main-wrapper">
                <div class="mb-4">
                    <div class="flex">
                        <h2 class="page-title"> Оформлення замовлення</h2>
                    </div>
                </div>

                <!-- Tab navigation -->
                <div v-if="hasBookings || hasProducts" class="flex border-b border-gray-200 mb-6">
                    <button
                        v-if="hasBookings"
                        @click="activeTab = 'bookings'"
                        class="py-2 px-4 text-sm font-medium border-b-2 -mb-px transition-colors"
                        :class="activeTab === 'bookings'
                            ? 'border-indigo-600 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'"
                    >
                        Бронювання ({{ form.items.length }})
                    </button>
                    <button
                        v-if="hasProducts"
                        @click="activeTab = 'products'"
                        class="py-2 px-4 text-sm font-medium border-b-2 -mb-px transition-colors"
                        :class="activeTab === 'products'
                            ? 'border-indigo-600 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'"
                    >
                        Товари ({{ productItems.length }})
                    </button>
                </div>

                <!-- BOOKINGS TAB -->
                <template v-if="activeTab === 'bookings'">
                    <div class="cart-item py-4" v-for="(item, index) in form.items" :key="index">
                        <div class="item-details flex justify-between items-start pb-8 border-b-2">
                            <div>
                                <h3 class="text-xl font-semibold">{{ item.name }}</h3>
                                <p class="text-sm text-gray-600">{{ item.attributes.date }}</p>
                                <p class="text-sm text-gray-600">Тариф: {{ item.attributes.category_name }}</p>
                                <p class="text-sm text-gray-600">Опис тарифа: {{ item.attributes.category_description }}</p>
                                <p class="text-sm text-gray-600">Час заїзду: {{ item.attributes.type_name }}</p>
                                <div class="flex items-center mt-6">
                                    <text-input
                                        v-if="priceDependOnPeopleCount(item)"
                                        v-model="item.attributes.quantity"
                                        type="number"
                                        min="1"
                                        max="99"
                                        :error="form.errors.quantity"
                                        class="w-full cart-people-quantity"
                                        label="Кількість людей"
                                        @input="updateCart(index)"
                                    />
                                </div>
                                <p class="mt-4 text-l text-gray-600"><b>Ціна:</b> {{ item.price }} грн</p>
                            </div>
                            <div>
                                <button
                                    class="items-center h-12 btn-indigo uppercase tracking-widest text-uppercase rounded-lg justify-center btn-delete-from-cart"
                                    @click="removeFromCart(item.id, index)">
                                </button>
                            </div>
                        </div>
                    </div>
                    <div>
                        <p class="text-2xl"><b>Всього:</b> {{ total }} грн</p>
                        <p v-if="userBalance > 0" class="text-sm text-indigo-600 mt-1">
                            Ваш баланс: <b>{{ userBalance.toFixed(2) }} грн</b>
                        </p>
                    </div>
                    <div class="mt-8 w-full lg:w-1/4">
                        <div class="mb-4">
                            <label for="name" class="block text-sm font-medium text-gray-700">Імʼя</label>
                            <input type="text" id="name" v-model="form.name" maxlength="25"
                                   class="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-sm p-2">
                            <div v-if="form.errors.name" class="form-error">{{ form.errors.name }}</div>
                        </div>
                        <div class="mb-4">
                            <label for="phone" class="block text-sm font-medium text-gray-700">Телефон</label>
                            <input type="tel" id="phone" v-model="form.phone" min="9"
                                   class="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-sm p-2"
                            >
                            <div v-if="form.errors.phone" class="form-error">{{ form.errors.phone }}</div>
                        </div>
                        <div class="mb-4">
                            <div class="flex">
                                <input type="checkbox" id="rules" v-model="form.rules" @change="clearRuleError">
                                <label for="rules" class="block text-sm font-medium text-gray-700 ml-2">
                                    Я ознайомився
                                    <span
                                        class="text-red-400"
                                        role="button"
                                        @click="showRuleModel"
                                    >
                                    з правилами сервісу
                                </span>
                                    та погоджуюся з ними.
                                </label>
                            </div>
                            <div v-if="form.errors.rules" class="form-error">{{ form.errors.rules }}</div>
                        </div>
                    </div>
                    <div class="flex flex-wrap gap-2 mt-4" :class="canPayCash ? 'justify-between' : 'justify-end'">
                        <loading-button
                            v-if="canPayCash"
                            class="items-center h-12 btn-indigo uppercase tracking-widest btn-booking text-uppercase rounded-lg justify-center"
                            :loading="loading"
                            @click="checkoutByCash"
                        >
                            Провести оплату
                        </loading-button>
                        <loading-button
                            v-if="canPayViaSubscription"
                            class="items-center h-12 btn-indigo uppercase tracking-widest btn-booking text-uppercase rounded-lg justify-center"
                            :loading="loading"
                            @click="checkoutBySubscription"
                        >
                            Підтвердіть за підпискою
                        </loading-button>
                        <loading-button
                            v-if="balanceCoversFull"
                            class="items-center h-12 btn-indigo uppercase tracking-widest btn-booking text-uppercase rounded-lg justify-center"
                            :loading="loading"
                            @click="checkoutViaBalance"
                        >
                            Сплатити з балансу ({{ total }} грн)
                        </loading-button>
                        <loading-button
                            class="items-center h-12 btn-indigo uppercase tracking-widest btn-booking text-uppercase rounded-lg justify-center"
                            :loading="loading"
                            @click="checkout"
                        >
                            Сплатити
                        </loading-button>
                    </div>
                </template>

                <!-- PRODUCTS TAB -->
                <template v-if="activeTab === 'products'">
                    <div class="cart-item py-4" v-for="(item, index) in productItems" :key="item.product_id">
                        <div class="item-details flex justify-between items-start pb-8 border-b-2">
                            <div class="flex gap-4">
                                <img
                                    v-if="item.image"
                                    :src="`/storage/${item.image}`"
                                    :alt="item.name"
                                    class="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                                />
                                <div v-else class="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 text-gray-400 text-xs text-center">
                                    Без фото
                                </div>
                                <div>
                                    <h3 class="text-xl font-semibold">{{ item.name }}</h3>
                                    <p class="text-sm text-gray-600 mt-1"><b>Ціна:</b> {{ item.price }} грн / шт.</p>
                                    <div class="flex items-center gap-2 mt-3">
                                        <label class="text-sm text-gray-600">Кількість:</label>
                                        <input
                                            v-model.number="item.quantity"
                                            type="number"
                                            min="1"
                                            :max="item.available"
                                            class="w-20 border-gray-300 rounded-md shadow-sm text-sm p-1 text-center"
                                            @input="updateProductCart(item.product_id, index)"
                                        />
                                    </div>
                                    <p class="mt-2 text-sm font-medium text-indigo-600">
                                        Разом: {{ (item.price * item.quantity).toFixed(2) }} грн
                                    </p>
                                </div>
                            </div>
                            <div>
                                <button
                                    class="items-center h-12 btn-indigo uppercase tracking-widest text-uppercase rounded-lg justify-center btn-delete-from-cart"
                                    @click="removeProductFromCart(item.product_id, index)">
                                </button>
                            </div>
                        </div>
                    </div>
                    <div>
                        <p class="text-2xl"><b>Всього:</b> {{ computedProductTotal }} грн</p>
                    </div>
                    <div class="mt-8 w-full lg:w-1/4">
                        <p class="mb-4 text-sm text-gray-500">
                            Залиште свої контакти — ми зв'яжемося з вами для підтвердження замовлення, адреси та способу доставки.
                        </p>
                        <div class="mb-4">
                            <label for="p-name" class="block text-sm font-medium text-gray-700">Імʼя</label>
                            <input type="text" id="p-name" v-model="productCheckoutForm.name" maxlength="25"
                                   class="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-sm p-2">
                            <div v-if="productCheckoutForm.errors.name" class="form-error">{{ productCheckoutForm.errors.name }}</div>
                        </div>
                        <div class="mb-4">
                            <label for="p-phone" class="block text-sm font-medium text-gray-700">Телефон</label>
                            <input type="tel" id="p-phone" v-model="productCheckoutForm.phone" min="9"
                                   class="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-sm p-2">
                            <div v-if="productCheckoutForm.errors.phone" class="form-error">{{ productCheckoutForm.errors.phone }}</div>
                        </div>
                        <div class="mb-4">
                            <label for="p-address" class="block text-sm font-medium text-gray-700">Адреса доставки</label>
                            <textarea id="p-address" v-model="productCheckoutForm.delivery_address" rows="3"
                                      class="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-sm p-2"
                                      placeholder="Залиште свої контакти — ми зв’яжемося з вами для підтвердження замовлення, адреси та способу доставки."></textarea>
                            <div v-if="productCheckoutForm.errors.delivery_address" class="form-error">{{ productCheckoutForm.errors.delivery_address }}</div>
                        </div>
                    </div>
                    <div class="flex flex-wrap gap-2 mt-4" :class="canPayCash ? 'justify-between' : 'justify-end'">
                        <loading-button
                            v-if="canPayCash"
                            class="items-center h-12 btn-indigo uppercase tracking-widest btn-booking text-uppercase rounded-lg justify-center"
                            :loading="loading"
                            @click="checkoutProductsByCash"
                        >
                            Провести оплату
                        </loading-button>
                        <loading-button
                            class="items-center h-12 btn-indigo uppercase tracking-widest btn-booking text-uppercase rounded-lg justify-center"
                            :loading="loading"
                            @click="checkoutProducts"
                        >
                            Сплатити
                        </loading-button>
                    </div>
                </template>
            </div>
        </div>
        <RulesModal
            :show="isRuleModelShown"
            :description="rules?.description"
            @update:show="isRuleModelShown = $event"
        />
    </div>
</template>

<script>
import {Head} from '@inertiajs/inertia-vue3'
import Header from "@/Component/Header"
import {useToast} from "vue-toastify"
import TextInput from "@/Shared/TextInput"
import {debounce} from "lodash"
import LoadingButton from "@/Shared/LoadingButton"
import RulesModal from "@/Component/RulesModal"

export default {
    components: {
        RulesModal,
        LoadingButton,
        TextInput,
        Header,
        Head,
    },
    props: {
        cartItems: Object,
        total: Number,
        productCartItems: { type: Array, default: () => [] },
        productTotal: { type: Number, default: 0 },
        user: Object,
        userBalance: { type: Number, default: 0 },
        rules: Object
    },
    data() {
        const hasBookings = Object.keys(this.cartItems).length > 0
        const hasProducts = this.productCartItems.length > 0
        return {
            form: this.$inertia.form({
                items: Object.values(this.cartItems).map(item => ({
                    ...item
                })),
                name: this.user ? this.user.first_name : '',
                phone: this.user ? this.user.phone_number : '',
                rules: false,
                errors: {
                    phone: ''
                }
            }),
            productItems: this.productCartItems.map(item => ({ ...item })),
            productCheckoutForm: this.$inertia.form({
                name: this.user ? this.user.first_name : '',
                phone: this.user ? this.user.phone_number : '',
                delivery_address: '',
            }),
            activeTab: !hasBookings && hasProducts ? 'products' : 'bookings',
            toast: null,
            cartCount: 0,
            loading: false,
            isRuleModelShown: false
        };
    },
    computed: {
        hasBookings() {
            return this.form.items.length > 0
        },
        hasProducts() {
            return this.productItems.length > 0
        },
        computedProductTotal() {
            return this.productItems
                .reduce((sum, item) => sum + item.price * item.quantity, 0)
                .toFixed(2)
        },
        canPayCash() {
            return this.user && (this.user.watchman || this.user.owner)
        },
        canPayViaSubscription() {
            if (!this.user?.annual_subscription) return false
            const config = this.$page.props.subscriptionConfig
            const allowedTypes = config?.allowed_type_ids ?? []
            const allowedCategories = config?.allowed_category_ids ?? []
            const allowedPoints = config?.allowed_point_ids ?? []
            if (!allowedTypes.length || !allowedCategories.length) return false
            return this.form.items.every(item => {
                if (!allowedTypes.includes(item.attributes.type_id)) return false
                if (!allowedCategories.includes(item.attributes.category_id)) return false
                if (allowedPoints.length) {
                    if (!item.attributes.point_id) return false
                    if (!allowedPoints.includes(item.attributes.point_id)) return false
                }
                return true
            })
        },
        balanceCoversFull() {
            return this.userBalance >= this.total
        },
    },
    methods: {
        // Booking tab methods
        clearRuleError() {
            if (this.form.rules) {
                this.form.errors.rules = null;
            }
        },
        showRuleModel() {
            this.clearRuleError()
            this.isRuleModelShown = !this.isRuleModelShown
        },
        priceDependOnPeopleCount(item) {
            return !!item.attributes.is_price_depend_on_people_count
        },
        validatePhone() {
            if (this.form.phone.length < 9) {
                this.form.errors.phone = 'Номер телефону має бути не меньш ніж 9 символів';
                return false;
            }
            this.form.errors.phone = '';
            return true;
        },
        updateCart: debounce(function (index) {
            if (this.form.items[index].attributes.quantity < 1) {
                return this.form.items[index].attributes.quantity = 1
            }
            this.form.put(this.$route('cart.update'))
        }, 200),
        removeFromCart(itemId, index) {
            this.form.items.splice(index, 1);
            this.form.delete(this.$route('cart.remove', itemId))
        },
        checkout() {
            if (this.loading) return;
            if (!this.form.rules) {
                this.form.errors.rules = 'Ви не погодилися з правилами використання сервіса'
                return
            }
            this.loading = true;
            if (this.validatePhone()) {
                this.form.post(this.$route('book.points'), {
                    onFinish: () => {
                        this.loading = false;
                    }
                });
            } else {
                this.loading = false;
            }
        },
        checkoutByCash() {
            if (this.loading) return;
            this.loading = true
            if (this.validatePhone()) {
                this.form.post(this.$route('book.points.cash'), {
                    onFinish: () => {
                        this.loading = false;
                    }
                });
            } else {
                this.loading = false;
            }
        },
        checkoutBySubscription() {
            if (this.loading) return;
            this.loading = true
            if (this.validatePhone()) {
                this.form.post(this.$route('book.points.subscription'), {
                    onFinish: () => {
                        this.loading = false;
                    }
                });
            } else {
                this.loading = false;
            }
        },
        checkoutViaBalance() {
            if (this.loading) return;
            if (!this.form.rules) {
                this.form.errors.rules = 'Ви не погодилися з правилами використання сервіса'
                return
            }
            this.loading = true
            if (this.validatePhone()) {
                this.form.post(this.$route('book.points.balance'), {
                    onFinish: () => { this.loading = false; }
                });
            } else {
                this.loading = false;
            }
        },

        // Products tab methods
        validateProductPhone() {
            if (!this.productCheckoutForm.phone || this.productCheckoutForm.phone.length < 9) {
                this.productCheckoutForm.errors.phone = 'Номер телефону має бути не меньш ніж 9 символів';
                return false;
            }
            this.productCheckoutForm.errors.phone = '';
            return true;
        },
        updateProductCart: debounce(function (productId, index) {
            if (this.productItems[index].quantity < 1) {
                this.productItems[index].quantity = 1
                return
            }
            this.$inertia.put(
                this.$route('cart.products.update', productId),
                { quantity: this.productItems[index].quantity },
                { preserveState: true, preserveScroll: true }
            )
        }, 300),
        removeProductFromCart(productId, index) {
            this.productItems.splice(index, 1)
            this.$inertia.delete(this.$route('cart.products.remove', productId))
        },
        checkoutProducts() {
            if (this.loading) return;
            this.loading = true
            if (this.validateProductPhone()) {
                this.productCheckoutForm.post(this.$route('products.cart.checkout'), {
                    onFinish: () => { this.loading = false; }
                })
            } else {
                this.loading = false;
            }
        },
        checkoutProductsByCash() {
            if (this.loading) return;
            this.loading = true
            if (this.validateProductPhone()) {
                this.productCheckoutForm.post(this.$route('products.cart.checkout.cash'), {
                    onFinish: () => { this.loading = false; }
                })
            } else {
                this.loading = false;
            }
        },
    },
    mounted() {
        this.toast = useToast()
    }
}
</script>
